import React, { useState, useMemo } from "react";
import { useBatch } from "../../context/BatchContext";
import SmartPicker from "../SmartPicker/SmartPicker";
import TaxonomyMetadataPicker from "../../../site-admin/components/TaxonomyMetadataPicker";
import { queueManager } from "../../services/queueManager";
import { deleteBatch } from "../../services/batchActions";

// --- NAVIGATION & CONTEXT IMPORTS ---
import { useNavigate } from "react-router-dom";
import { useNavigationContext } from "../../../common/navigation/NavigationContext";

export default function BatchMonitorView() {
  const { activeBatches, refreshAllBatches, uploadProgress } = useBatch();
  const [expandedId, setExpandedId] = useState(null);
  const [pausedStates, setPausedStates] = useState({});
  const [filter, setFilter] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10); // Standard boilerplate density

  const navigate = useNavigate();
  const { activeWorkflow } = useNavigationContext();
  const [siteMetaData, setSiteMetaData] = useState({});

  // --- LOGIC HELPERS ---
  const formatBytes = (bytes, decimals = 2) => {
    if (!+bytes) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  // --- ACTION HANDLERS ---
  const handleTogglePause = (batchId) => {
    queueManager.togglePause(batchId);
    setPausedStates((prev) => ({ ...prev, [batchId]: !prev[batchId] }));
  };

  const handleCancelBatch = async (batchId) => {
    const siteKey = process.env.REACT_APP_SITE_KEY;
    queueManager.cancelBatch(batchId);
    await deleteBatch(batchId, siteKey);
    await refreshAllBatches();
  };

  const handleFilePause = (batchId, fileName) => {
    queueManager.toggleFilePause(batchId, fileName);
    refreshAllBatches();
  };

  const handleFileReset = async (batchId, fileName) => {
    const siteKey = process.env.REACT_APP_SITE_KEY;
    await queueManager.stopAndResetFile(batchId, fileName, siteKey);
    refreshAllBatches();
  };

  const handleFileCancel = async (batchId, fileName) => {
    const siteKey = process.env.REACT_APP_SITE_KEY;
    if (window.confirm(`Permanently remove ${fileName} from this batch?`)) {
      await queueManager.cancelFile(batchId, fileName, siteKey);
      refreshAllBatches();
    }
  };

  // --- FILTERING LOGIC ---
  const filteredBatches = useMemo(() => {
    return activeBatches.filter((b) =>
      b.batchId.toString().includes(filter) || 
      b.status.toLowerCase().includes(filter.toLowerCase())
    );
  }, [activeBatches, filter]);

  return (
    <div className="page-main mt-5 pt-5" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section px-4">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-xl-8">
                
                {/* 1. HERO / HEADER BLOCK */}
                <div className="d-flex justify-content-between align-items-end mb-4">
                  <div>
                    <h2 className="h4 fw-bold mb-1 text-dark">ESRS Taxonomy Sync</h2>
                    <p className="text-muted extra-small mb-0 text-uppercase fw-bold">
                      Batch Ingestion Authority
                    </p>
                  </div>
                  <div className="text-end">
                    <span className="badge bg-dark extra-small font-monospace">
                      ACTIVE BATCHES: {activeBatches.length}
                    </span>
                  </div>
                </div>

                {/* 2. PICKER SECTION */}
                <div className="mb-5">
                  <SmartPicker
                    MetadataComponent={TaxonomyMetadataPicker}
                    siteMetaData={siteMetaData}
                    setSiteMetaData={setSiteMetaData}
                    batchType={1}
                  />
                </div>

                {/* 3. CONTROL BAR (Filter & Density) */}
                <div className="d-flex gap-3 mb-3 mt-5">
                  <div className="card shadow-sm border-0 bg-white flex-grow-1">
                    <div className="card-body p-2">
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-transparent border-0 text-muted">
                          <i className="bi bi-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-0 shadow-none"
                          placeholder="Filter batches by ID or Status..."
                          onChange={(e) => setFilter(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="card shadow-sm border-0 bg-white" style={{ minWidth: "140px" }}>
                    <div className="card-body p-2 d-flex align-items-center">
                      <span className="extra-small text-muted me-2 text-uppercase fw-bold">View:</span>
                      <select 
                        className="form-select form-select-sm border-0 shadow-none extra-small fw-bold"
                        value={itemsPerPage}
                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      >
                        <option value={10}>10 Rows</option>
                        <option value={20}>20 Rows</option>
                        <option value={50}>50 Rows</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* 4. ACTIVE TRANSFERS SECTION */}
                <h6 className="fw-bold mb-3 text-uppercase small text-muted">
                  Active Transfers
                </h6>

                {filteredBatches.slice(0, itemsPerPage).map((batch) => {
                  const isBatchComplete = batch.status === "Delivered";

                  return (
                    <div
                      key={batch.batchId}
                      className={`card shadow-sm border-0 mb-3 bg-white border-start border-4 ${
                        isBatchComplete
                          ? "border-success"
                          : pausedStates[batch.batchId]
                            ? "border-warning"
                            : "border-dark"
                      }`}
                    >
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div
                            style={{ cursor: "pointer" }}
                            onClick={() => setExpandedId(expandedId === batch.batchId ? null : batch.batchId)}
                          >
                            <span className="fw-bold small d-block">
                              {expandedId === batch.batchId ? "▼" : "▶"} Batch #{batch.batchId}
                              {isBatchComplete && (
                                <span className="badge bg-success ms-2 extra-small text-white">COMPLETED</span>
                              )}
                              {pausedStates[batch.batchId] && !isBatchComplete && (
                                <span className="badge bg-warning text-dark ms-2 extra-small">PAUSED</span>
                              )}
                            </span>
                            <span className="text-muted extra-small">
                              {batch.status} • {batch.files?.length} files
                            </span>
                          </div>

                          {!isBatchComplete && (
                            <div className="d-flex gap-2">
                              <button
                                className={`btn btn-sm fw-bold extra-small ${pausedStates[batch.batchId] ? "btn-outline-success" : "btn-outline-warning"}`}
                                onClick={() => handleTogglePause(batch.batchId)}
                              >
                                {pausedStates[batch.batchId] ? "RESTART" : "PAUSE"}
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger fw-bold extra-small"
                                onClick={() => handleCancelBatch(batch.batchId)}
                              >
                                CANCEL
                              </button>
                            </div>
                          )}
                        </div>

                        {expandedId === batch.batchId && (
                          <div className="mt-3 pt-3 border-top">
                            <ul className="list-unstyled mb-0">
                              {batch.files?.map((f, i) => {
                                const progress = uploadProgress[`${batch.batchId}-${f.name}`] || f.progress || 0;
                                const isFilePaused = queueManager.pausedFiles?.has(`${batch.batchId}-${f.name}`);
                                const isFileComplete = f.status === "Delivered" || progress === 100;

                                return (
                                  <li key={i} className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <span className="text-truncate extra-small text-muted" style={{ maxWidth: "45%" }}>
                                        {f.name}
                                      </span>
                                      <div className="d-flex gap-3 align-items-center">
                                        {!isFileComplete && !isBatchComplete && (
                                          <>
                                            <button
                                              className={`btn btn-link p-0 extra-small fw-bold text-decoration-none ${isFilePaused ? "text-success" : "text-warning"}`}
                                              onClick={() => handleFilePause(batch.batchId, f.name)}
                                            >
                                              {isFilePaused ? "CONTINUE" : "PAUSE"}
                                            </button>
                                            <button
                                              className="btn btn-link p-0 text-muted extra-small fw-bold text-decoration-none"
                                              onClick={() => handleFileReset(batch.batchId, f.name)}
                                            >
                                              STOP
                                            </button>
                                            <button
                                              className="btn btn-link p-0 text-danger extra-small fw-bold text-decoration-none"
                                              onClick={() => handleFileCancel(batch.batchId, f.name)}
                                            >
                                              CANCEL
                                            </button>
                                          </>
                                        )}
                                        <span className="extra-small fw-bold" style={{ minWidth: "35px" }}>
                                          {progress}%
                                        </span>
                                      </div>
                                    </div>
                                    <div className="progress" style={{ height: "8px", backgroundColor: "#f8f9fa" }}>
                                      <div
                                        className={`progress-bar progress-bar-striped progress-bar-animated ${isFilePaused ? "bg-warning" : f.status === "Failed" ? "bg-danger" : "bg-success"}`}
                                        role="progressbar"
                                        style={{
                                          width: `${progress}%`,
                                          transition: "width 0.4s linear",
                                          willChange: "width",
                                        }}
                                      ></div>
                                    </div>
                                    <div className="d-flex justify-content-between mt-1">
                                      <small className="text-muted extra-small">
                                        {formatBytes(f.bytesUploaded || (progress === 100 ? f.size : 0))} / {formatBytes(f.size)}
                                      </small>
                                      {f.status === "Failed" && <small className="text-danger extra-small">Upload failed</small>}
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {filteredBatches.length === 0 && (
                  <div className="text-center p-5 text-muted small italic">
                    No active transfers found.
                  </div>
                )}

                {/* 5. STANDARD ACTION FOOTER */}
                <div className="mt-5 pt-4 border-top d-flex justify-content-between align-items-center">
                  <button 
                    type="button"
                    className="btn btn-link text-decoration-none p-0 text-secondary small d-flex align-items-center" 
                    onClick={() => navigate(activeWorkflow?.path || "/registered/site-admin")}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Return to {activeWorkflow?.name || "Previous Page"}
                  </button>
                  
                  <div className="text-end">
                    <span className="text-muted extra-small d-block">
                      System Authority: <strong>Operational</strong>
                    </span>
                    <span className="text-muted extra-small">
                      Showing <strong>{Math.min(filteredBatches.length, itemsPerPage)}</strong> of <strong>{filteredBatches.length}</strong> active batches
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}